import { Core } from "@faire/design-tokens";
import { Avatar, AvatarSize } from "@faire/web--source/slate/Avatar";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { SpacerValueOrKey } from "@faire/web--source/slate/spacing/getSpacing";
import { ResponsiveProp } from "@faire/web--source/slate/Theme/ResponsiveProp";
import { Typography } from "@faire/web--source/slate/Typography";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React from "react";
import styled, { css } from "styled-components";

interface IProps {
  images: IImage[];
  subtitle: string;
  title?: string;
  size?: ResponsiveProp<AvatarSize>;
  maxTextWidth?: number;
  className?: string;
  spacingAboveTitle?: ResponsiveProp<SpacerValueOrKey>;
  /** When there are multiple avatar images that render in a "pile" stacked up with each other, how much offset should there be between each avatar image */
  avatarPileOffset?: number;
  onClick?: () => void;
}

const MAX_AVATAR_COUNT = 3;

export const TeamMemberUserInfo = ({
  className,
  images,
  title,
  subtitle,
  size,
  maxTextWidth,
  spacingAboveTitle,
  avatarPileOffset,
  onClick,
}: IProps) => {
  const avatarPileLeftMargin = avatarPileOffset ? -avatarPileOffset : -48;
  return (
    <UserDataWrapper className={className}>
      <Row>
        {images
          .slice(0, MAX_AVATAR_COUNT)
          .reverse()
          .map((image, index) => {
            return (
              <AvatarWrapper
                key={index}
                $marginLeft={index === 0 ? 0 : avatarPileLeftMargin}
                $showPointerOnHover={!!onClick}
                onClick={onClick}
              >
                <TeamMemberAvatar
                  $hasBorder={images.length > 1}
                  size={size ?? "large"}
                  image={
                    typeof image === "string"
                      ? IImage.build({
                          url: image,
                        })
                      : image
                  }
                />
              </AvatarWrapper>
            );
          })}
      </Row>
      <Spacer height={spacingAboveTitle ?? "1x"} />
      <TextWrapper $maxWidth={maxTextWidth ?? 100}>
        <Typography align="center" variant="paragraphSansMedium" maxLines={3}>
          {title}
        </Typography>
        <Typography align="center" maxLines={3}>
          {subtitle}
        </Typography>
      </TextWrapper>
    </UserDataWrapper>
  );
};

const UserDataWrapper = styled(Column).attrs({ align: "center" })`
  flex: 1;
`;

const AvatarWrapper = styled.div<{
  $marginLeft: number;
  $showPointerOnHover?: boolean;
}>`
  margin-left: ${({ $marginLeft }) => $marginLeft}px;
  ${({ $showPointerOnHover: pointerOnHover }) =>
    pointerOnHover &&
    css`
      &:hover {
        cursor: pointer;
      }
    `}
`;

const TextWrapper = styled.div<{ $maxWidth: number }>`
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  word-break: break-word;
`;

const TeamMemberAvatar = styled(Avatar)<{ $hasBorder: boolean }>`
  ${(props) =>
    props.$hasBorder
      ? css`
          border-left: 1px solid ${Core.surface.subdued};
          border-bottom: 1px solid ${Core.surface.subdued};
          border-top: 1px solid ${Core.surface.subdued};
        `
      : ""}
`;
